import { useState } from 'react';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Mail, Phone, Send, Instagram, Youtube } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/lib/supabaseClient';

const Contact = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const { error } = await supabase.from('leads').insert([{
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: formData.message,
        status: 'pending'
      }]);

      if (error) throw error;

      toast({
        title: 'Transmission Successful',
        description: "Your signal has been received. Our analysts will reach out within 24 hours.",
      });

      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error: any) {
      console.error('Error submitting lead:', error);
      toast({
        title: 'Signal Interruption',
        description: "Failed to transmit message. Technical protocols breached.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };


  const contactInfo = [
    {
      icon: Mail,
      title: 'Email',
      value: 'ratchagananalysis@gmail.com',
      href: 'mailto:ratchagananalysis@gmail.com',
    },
    {
      icon: Phone,
      title: 'Contact Number',
      value: '+91 96633 06819',
      href: 'tel:9663306819',
    },
  ];

  const supportOptions = [
    { title: 'Live Support Access', description: 'Real-time help for your trading journey' },
    { title: 'Helpdesk', description: 'Swift help for technical and account questions' },
    { title: 'Mentor Support', description: 'Direct access to expert trading help' },
  ];

  const socialLinks = [
    { icon: Instagram, href: 'https://www.instagram.com/ratchagananalysis/', label: 'Instagram' },
    { icon: Youtube, href: 'https://www.youtube.com/@ratchagananalysis', label: 'YouTube' },
    { icon: Send, href: 'https://t.me/ratchagananalysis', label: 'Telegram' },
  ];

  return (
    <Layout>
      <section className="relative overflow-hidden bg-background pt-8 pb-12">
        <div className="absolute inset-0 z-0">
          <div className="absolute top-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-accent/5 blur-[120px]" />
        </div>

        <div className="container-max text-center relative z-10">
          <span className="inline-block px-4 py-2 bg-accent/5 border border-accent/20 rounded-full text-accent-foreground text-[10px] font-black uppercase tracking-[0.2em] mb-8">
            Contact
          </span>
          <h1 className="text-2xl md:text-4xl font-black text-primary mb-6 tracking-tighter uppercase">
            Contact <span className="text-gradient-gold">Us</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto font-medium leading-relaxed">
            Have a question? Send us a message and we’ll reply soon.
          </p>
        </div>
      </section>

      <section className="section-padding bg-background relative overflow-hidden">
        <div className="absolute bottom-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-primary/3 blur-[120px] pointer-events-none" />

        <div className="container-max relative z-10">
          <div className="grid lg:grid-cols-2 gap-20">
            <div className="space-y-12">
              <div>
                <h2 className="text-3xl font-black text-primary uppercase tracking-tighter mb-8">Contact Details</h2>
                <div className="space-y-6">
                  {contactInfo.map((info, index) => (
                    <a
                      key={index}
                      href={info.href}
                      className="flex items-start gap-6 p-8 rounded-[2rem] bg-white/40 border border-white/60 hover:bg-white hover:shadow-premium transition-all duration-500 group"
                    >
                      <div className="w-14 h-14 rounded-2xl bg-white shadow-soft flex items-center justify-center flex-shrink-0 group-hover:bg-accent transition-all duration-500 border border-gray-100">
                        <info.icon className="w-6 h-6 text-primary group-hover:text-accent-foreground transition-all duration-500" />
                      </div>
                      <div>
                        <p className="text-[10px] font-black uppercase tracking-widest text-accent mb-2">{info.title}</p>
                        <p className="text-lg font-bold text-primary">{info.value}</p>
                      </div>
                    </a>
                  ))}
                </div>
              </div>

              <div className="pt-4">
                <h3 className="text-xs font-black uppercase tracking-[0.3em] text-primary mb-8">Support Options</h3>
                <div className="grid gap-4">
                  {supportOptions.map((option, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-4 p-5 rounded-2xl bg-primary/5 border border-primary/5 group hover:bg-primary/10 transition-all duration-300"
                    >
                      <div className="w-10 h-10 rounded-xl bg-white shadow-sm flex items-center justify-center border border-gray-100 group-hover:bg-accent transition-colors duration-500 text-primary group-hover:text-accent-foreground">
                        {index === 0 ? <Phone className="w-5 h-5" /> : index === 1 ? <Send className="w-5 h-5" /> : <Mail className="w-5 h-5" />}
                      </div>
                      <div>
                        <h4 className="text-sm font-black text-primary uppercase tracking-tight">{option.title}</h4>
                        <p className="text-[10px] font-medium text-muted-foreground">{option.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-xs font-black uppercase tracking-[0.3em] text-primary mb-6">Social Links</h3>
                <div className="flex gap-4">
                  {socialLinks.map((social, index) => (
                    <a
                      key={index}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-14 h-14 rounded-full border border-gray-200 flex items-center justify-center hover:bg-primary hover:border-primary hover:text-white transition-all duration-300 group shadow-sm bg-white"
                      aria-label={social.label}
                    >
                      <social.icon className="w-5 h-5 transition-transform group-hover:scale-110" />
                    </a>
                  ))}
                </div>
              </div>
            </div>

            <div className="card-premium !p-12 bg-white/60 border-white/60 backdrop-blur-xl shadow-premium">
              <h2 className="text-3xl font-black text-primary uppercase tracking-tighter mb-10">Send a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-8">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label htmlFor="name" className="text-[10px] font-black uppercase tracking-widest text-primary/60 ml-1">
                      Name
                    </label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Enter your name"
                      required
                      className="h-14 bg-white/50 border-gray-200 focus:border-accent rounded-xl"
                    />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="phone" className="text-[10px] font-black uppercase tracking-widest text-primary/60 ml-1">
                      Mobile Number
                    </label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+91 0000 0000 00"
                      required
                      className="h-14 bg-white/50 border-gray-200 focus:border-accent rounded-xl"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <label htmlFor="email" className="text-[10px] font-black uppercase tracking-widest text-primary/60 ml-1">
                    Email
                  </label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="yourname@email.com"
                    required
                    className="h-14 bg-white/50 border-gray-200 focus:border-accent rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="message" className="text-[10px] font-black uppercase tracking-widest text-primary/60 ml-1">
                    Message
                  </label>
                  <Textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="Tell us how we can help..."
                    required
                    rows={5}
                    className="resize-none bg-white/50 border-gray-200 focus:border-accent rounded-xl p-4"
                  />
                </div>
                <Button
                  type="submit"
                  className="btn-gold h-16 w-full text-xs font-black uppercase tracking-[0.2em] group"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                  <Send className="w-4 h-4 ml-2 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                </Button>
                <div className="mt-6 p-4 rounded-xl bg-red-50/50 border border-red-100 flex items-center justify-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                  <p className="text-[10px] font-black uppercase tracking-widest text-red-600/80">Refund Policy: No refund after payment</p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Contact;
